import type { CSSProperties } from '@linaria/core';
import { cx } from '@linaria/core';
import { FunctionComponent, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { VList, VListHandle } from 'virtua';
import { AppStateType, DisplayModeFriends, DisplayUeopt, GridLocation, SettingsType } from '../../../constants/enums';
import { GridDensityUeopt } from '../../../mfcLogic/ueopts';
import { APPSTATE, HISTORY } from '../../../state';
import { clearAllPreviews } from '../../../state/modals/state.floater';
import { resetAvatarInfoOpenIDState, resetExploreScrollPositions } from '../../../state/state.grid';
import { AppRouter } from '../../../state/state.router';
import { isTouchDevice } from '../../../utilities/browser';
import { safeObj } from '../../../utilities/safe';
import ButtonDisplayType from '../../Buttons/ButtonDisplayType';
import { useAppState } from '../../Hooks/useAppState';
import { getPassiveState, usePassive } from '../../Hooks/usePassive';
import { usePreservePageState } from '../../Hooks/usePreservePageState';
import { useRouteSelector } from '../../Hooks/useRoute';
import { useScreenStateSelector } from '../../Hooks/useScreenState';
import { useSettingSelector } from '../../Hooks/useSettings';
import { useUserSelector } from '../../Hooks/useUser';
import { getDisplayHeight, getGridPadding, getGridSpecs, getScale, getStyle, GetStylesConfig } from './helpers';
import { useKeyboardNavigation } from './useKeyboardNavigation';
import "./index.linaria.module.css!=!./index?./index.linaria.module.css?css=H4sIAAAAAAAAA22PQQ6CMBRE997ChYkuMLQJLOphmiat9gvlQ/m0gca7m4AoRnczmZeZzFmRkzWTrAhdjP78bRMG4681xmwUFrQ2zeWxmxnDJdNV0YFK1sDNkgjKH7NsMacN1g5TidVfag+uRU+qoZUPo2TYd8hDiqDJCpbnh03op4E8f2VL16zfg66Sljt3L1OLPRBgI7ypFUEwK1L1P/c+557hmyk9EAEAAA=="
const noOverflowY = "atm_l1_15vqwwr";
const stylesPadding = "atm_e2_1dk5qia";
const stylesHeightOverride = "atm_e2_1dk5qia";
const stylesVList = "atm_e2_puz6ok atm_vy_1osqo2v";
const stylesRoot = "atm_vy_rzutr2 atm_mk_h2mmj6 atm_ks_15vqwwr";
interface RowProps {
  index: number;
  data: any;
}
const Row = memo((props: RowProps) => {
  const {
    index,
    data
  } = props;
  const {
    rowComponentProps,
    RowComponent,
    padding
  } = data;
  if (padding) {
    const paddingHeight = typeof padding === 'number' ? padding : 30;
    return <div style={{
      '--height': paddingHeight + 'px'
    } as CSSProperties} className={stylesPadding} />;
  }
  return <div data-testid={rowComponentProps?.category ? `Row__${rowComponentProps?.category}` : `Row__${index}`} className={cx('no-scrollbars', noOverflowY)}>
            <RowComponent {...rowComponentProps} index={index} />
        </div>;
});
interface OnScrollProps {
  scrollOffset: number;
  averageRowHeight: number;
  greatestRowHeight: number;
  totalRowsHeight: number;
  containerHeight: number;
  rowCount: number;
}
interface CoreProps {
  containerHeight: number;
  containerWidth: number;
  enableOnScroll?: boolean;
  displayHeight: number;
  gridDensity: GridDensityUeopt;
  gridLocation?: GridLocation;
  gridPadding: number;
  columnWidth: number;
  columnCount: number;
  DisplayTypeButton?: FunctionComponent<any>;
  displayTypeButtonProps?: any;
  platform: 'mobile' | 'desktop';
  onScroll?: (props: OnScrollProps) => void;
  getStyle: (key: string, config?: GetStylesConfig) => any;
  getRows: (info: any) => any;
  display: string;
  view?: string;
  scale: 'sm' | 'md' | 'lg';
  level?: number;
  username?: string;
  data: any;
}
const Core = memo((props: CoreProps) => {
  const {
    DisplayTypeButton = ButtonDisplayType,
    displayTypeButtonProps = {},
    containerHeight,
    containerWidth,
    enableOnScroll,
    displayHeight,
    gridLocation,
    gridDensity,
    gridPadding,
    columnWidth,
    columnCount,
    platform,
    onScroll,
    getStyle,
    getRows,
    display,
    level,
    username,
    view,
    scale,
    data
  } = props;
  const currentRoute = useRouteSelector('visibleRoute');
  const {
    isMobile
  } = useAppState(AppStateType.APP);
  const previousDisplay = useRef(display);
  const previousView = useRef(view);
  const refScrollTop = useRef<number>(0);
  const listRef = useRef<VListHandle>(null);
  const {
    positionTop
  } = safeObj(usePreservePageState({
    disablePreserveScrollPosition: gridLocation !== GridLocation.Home,
    displayMode: display as DisplayUeopt,
    refScrollTop,
    gridLocation,
    gridDensity,
    listRef
  }));
  const rows = useMemo(() => getRows({
    containerHeight,
    containerWidth,
    displayHeight,
    gridPadding,
    gridDensity,
    columnWidth,
    columnCount,
    platform,
    getStyle,
    isMobile,
    display,
    scale,
    level,
    username,
    view,
    data
  }), [containerHeight, containerWidth, displayHeight, gridPadding, gridDensity, columnWidth, columnCount, isMobile, platform, getStyle, display, getRows, scale, level, username, view, data]);
  useEffect(() => {
    if (!listRef.current || getPassiveState()) {
      return;
    }
    const previousRoute = HISTORY.lastRoute[1];
    const currentPage = AppRouter.route.visiblePage;
    let scrollToTop = false;
    if (previousRoute.visibleRoute !== currentRoute) {
      if (!positionTop &&
      // don't scroll down if we're navigating to a room
      !(currentPage === 'room' || currentPage === 'message' || currentPage === 'profile')) {
        scrollToTop = true;
      }
    }
    if (previousDisplay.current !== display) {
      previousDisplay.current = display;
      scrollToTop = true;
    }
    if (previousView.current !== view) {
      previousView.current = view;
      scrollToTop = true;
    }
    if (scrollToTop) {
      console.log('[scrolling to top]');
      listRef.current.scrollTo(0);
    }
  }, [currentRoute, columnWidth, columnCount, gridPadding, display, view, rows]);
  const totalRowsHeight = useMemo(() => {
    return rows.reduce((memo, {
      rowHeight = 0
    }) => memo + rowHeight, 0);
  }, [rows]);
  const greatestRowHeight = useMemo(() => {
    return rows.reduce((memo, {
      rowHeight = 0
    }) => {
      if (rowHeight > memo) {
        return rowHeight;
      }
      return memo;
    }, 0);
  }, [rows]);
  const rowCount = rows.length;
  const averageRowHeight = useMemo(() => {
    if (!rowCount) {
      if (totalRowsHeight) {
        return totalRowsHeight;
      }
      return 0;
    }
    return totalRowsHeight && rowCount && totalRowsHeight / rowCount;
  }, [totalRowsHeight, rowCount]);
  const rowComponents = useMemo(() => rows.map((row, index) => <Row key={row.itemKey || index} index={index} data={row} />), [rows]);

  // scroll grid up/down using arrow keys, page up/down, home/end
  useKeyboardNavigation({
    listRef,
    averageRowHeight,
    containerHeight,
    totalRowsHeight
  });
  const onScrollCallback = useCallback((scrollOffset: number) => {
    refScrollTop.current = scrollOffset;
    if (typeof onScroll !== 'function' || !enableOnScroll) {
      return;
    }
    onScroll({
      scrollOffset,
      greatestRowHeight,
      averageRowHeight,
      totalRowsHeight,
      containerHeight,
      rowCount
    });
  }, [greatestRowHeight, averageRowHeight, totalRowsHeight, containerHeight, enableOnScroll, rowCount, onScroll]);
  useEffect(() => {
    if (listRef.current && positionTop) {
      listRef.current.scrollTo(positionTop);
    }
  }, []);
  return <>
            <VList className={cx('live-preview-grid no-scrollbars', stylesVList)} style={{
      '--height': containerHeight + 'px'
    } as CSSProperties} onScroll={onScrollCallback} itemSize={averageRowHeight || undefined}
    // overscan={2}
    ref={listRef}>
                {rowComponents}
            </VList>
            <DisplayTypeButton display={display} gridDensity={gridDensity} platform={platform} {...displayTypeButtonProps} />
        </>;
});
interface GridProps {
  displayOverride?: DisplayUeopt | DisplayModeFriends;
  enableOnScroll?: boolean;
  enableSlide?: boolean;
  gridDensityOverride?: GridDensityUeopt;
  DisplayTypeButton?: FunctionComponent<any>;
  displayTypeButtonProps?: any;
  gridLocation?: GridLocation;
  overrideHeight?: number;
  onScroll?: (props: OnScrollProps) => void;
  onColumnCount?: (count: number) => any;
  getRows: (info: any) => any;
  data: any;
}
const Grid = memo((props: GridProps) => {
  const {
    gridDensityOverride,
    displayOverride,
    DisplayTypeButton,
    displayTypeButtonProps,
    enableOnScroll,
    enableSlide,
    gridLocation,
    overrideHeight,
    onColumnCount,
    onScroll,
    getRows,
    data
  } = props;
  const level = useUserSelector('me', 'level');
  const username = useUserSelector('me', 'name');
  const [displayUeopt] = useSettingSelector(SettingsType.DEFAULT, 'display');
  const [gridDensityUeopt] = useSettingSelector(SettingsType.DEFAULT, 'gridDensity');
  const display = displayOverride ?? displayUeopt;
  const gridDensity = gridDensityOverride ?? gridDensityUeopt;
  const {
    type: view,
    view: exploreView
  } = useRouteSelector('baseQuery');
  const mobileView = APPSTATE.app.isMobile; // this is safe because this never changes after initial load
  const isExploreView = gridLocation === GridLocation.Search && !view;
  const scale = getScale(gridDensity);
  const gridPadding = getGridPadding(scale);
  const contentWidth = useScreenStateSelector('contentWidth');
  const contentHeight = useScreenStateSelector('contentHeight');
  const windowHeight = useScreenStateSelector('windowHeight');
  const passiveMode = usePassive();
  const usableHeight =
  // friends and rooms have grids that aren't full height, so we should use the passed in override height
  overrideHeight ? overrideHeight : passiveMode ? windowHeight : contentHeight;
  const usableWidth = data.activeRoom ? data.roomWidth : contentWidth;
  useEffect(() => {
    resetAvatarInfoOpenIDState();
    resetExploreScrollPositions();
    // setLargePreviewPinnedIDState(null)
    clearAllPreviews();
    return () => {
      resetAvatarInfoOpenIDState();
      resetExploreScrollPositions();
      // setLargePreviewPinnedIDState(null)
      clearAllPreviews();
    };
  }, []);
  useEffect(() => {
    resetExploreScrollPositions();
  }, [isExploreView]);

  //
  // Marc (07/29/2023): I should probably include a tablet view:
  // (!isMobile() && isTouchDevice)
  //
  const platform = mobileView ? 'mobile' : 'desktop';
  const {
    columnWidth,
    columnCount
  } = getGridSpecs({
    isTouchDevice: isExploreView ? isTouchDevice : false,
    gridPadding,
    enableSlide,
    platform,
    display,
    scale,
    width: usableWidth
  });
  useEffect(() => {
    if (typeof onColumnCount !== 'function') {
      return;
    }
    onColumnCount(columnCount);
    return () => {
      onColumnCount(null);
    };
  }, [onColumnCount, columnCount]);
  const displayHeight = getDisplayHeight({
    columnWidth: columnWidth - gridPadding,
    display
  });
  const styles = useMemo(() => getStyle(platform, {}, {
    gridPadding,
    columnWidth,
    columnCount
  })(scale)(display), [gridPadding, columnWidth, columnCount, platform, display, scale]);

  // console.log('[Grid]', {
  //     usableWidth,
  //     // gridPadding,
  //     // columnWidth,
  //     // columnCount,
  //     displayHeight,
  //     gridLocation,
  // })

  const isOverrideHeightDefined = typeof overrideHeight === 'number';
  return <div style={{
    ...(isOverrideHeightDefined ? {
      '--height': overrideHeight + 'px'
    } : {}),
    '--width': usableWidth + 'px'
  } as CSSProperties} className={cx(stylesRoot, isOverrideHeightDefined && stylesHeightOverride)} id="grid-root">
            <Core getStyle={styles} enableOnScroll={enableOnScroll} displayHeight={displayHeight} DisplayTypeButton={DisplayTypeButton} displayTypeButtonProps={displayTypeButtonProps} gridDensity={gridDensity} gridLocation={gridLocation} gridPadding={gridPadding} columnWidth={columnWidth} columnCount={columnCount} containerHeight={usableHeight} containerWidth={usableWidth} platform={platform} onScroll={onScroll} getRows={getRows} display={display} view={exploreView} level={level} username={username} scale={scale} data={data} />
        </div>;
});
export default Grid;
export { Grid };